import React, { useState, useEffect } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, convertFromMXN } from '../../utils/formatters';
import { Target, DollarSign, Calendar, ChevronRight, ChevronLeft, Plus, Minus, TrendingUp, Zap, Award, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { calculateResults } from '../../utils/calculations';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';
import { compoundMultiplierContent } from '../../data/compoundMultiplierContent';

interface ReinvestmentInvestorGoalsProps {
  onNext: () => void;
  onPrevious: () => void;
}

const ReinvestmentInvestorGoals: React.FC<ReinvestmentInvestorGoalsProps> = ({ onNext, onPrevious }) => {
  const { investment, updateInvestment } = useCalculator();
  
  // Estados para las metas del inversionista
  const [monthlyGoal, setMonthlyGoal] = useState(investment.investorMonthlyGoal || 50000);
  const [timeframe, setTimeframe] = useState(investment.investorTimeframe || 15);
  const [calculatedCertificates, setCalculatedCertificates] = useState(investment.initialCertificates);
  const [isCalculating, setIsCalculating] = useState(false);
  const [resultsWithMultiplier, setResultsWithMultiplier] = useState<any>(null);
  const [resultsWithoutMultiplier, setResultsWithoutMultiplier] = useState<any>(null);

  // Variable para el estado actual de reinversión
  const currentReinvestmentSetting = investment.reinvestProfits;

  // Presets para metas mensuales
  const monthlyGoalPresets = [
    { label: 'Básico', value: 30000, description: 'Ingreso complementario' },
    { label: 'Estándar', value: 50000, description: 'Ingreso principal' },
    { label: 'Premium', value: 100000, description: 'Estilo de vida premium' },
    { label: 'Elite', value: 200000, description: 'Libertad financiera' }
  ];

  // Presets para plazos
  const timeframePresets = [
    { label: 'Corto', value: 10, description: '10 años' },
    { label: 'Medio', value: 15, description: '15 años' },
    { label: 'Largo', value: 20, description: '20 años' },
    { label: 'Muy Largo', value: 25, description: '25 años' }
  ];

  // Función para calcular certificados necesarios usando búsqueda binaria
  const calculateRequiredCertificates = (goal: number, years: number) => {
    let minCerts = 1;
    let maxCerts = 20;
    let bestCerts = 1;
    
    // Búsqueda binaria para encontrar el número óptimo de certificados
    while (minCerts <= maxCerts) {
      const midCerts = Math.floor((minCerts + maxCerts) / 2);
      
      try {
        const testInvestment = {
          ...investment,
          initialCertificates: midCerts,
          years: years,
          reinvestProfits: currentReinvestmentSetting
        };
        
        const results = calculateResults(testInvestment);
        const finalMonthlyIncome = convertFromMXN(results.finalMonthlyIncome, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
        
        if (finalMonthlyIncome >= monthlyGoal) {
          bestCerts = midCerts;
          maxCerts = midCerts - 1; // Buscar si podemos con menos certificados
        } else {
          minCerts = midCerts + 1; // Necesitamos más certificados
        }
      } catch (error) {
        console.error('Error calculating certificates:', error);
        break;
      }
    }
    
    return Math.max(1, Math.min(20, bestCerts));
  };

  // Calcular resultados reales cuando cambien los parámetros
  useEffect(() => {
    setIsCalculating(true);
    
    const timer = setTimeout(() => {
      try {
        const required = calculateRequiredCertificates(monthlyGoal, timeframe);
        setCalculatedCertificates(required);
        
        // Calcular resultados CON Multiplicador™ (con reinversión)
        const testInvestmentWith = {
          ...investment,
          initialCertificates: required,
          years: timeframe,
          reinvestProfits: true
        };
        const resultsWith = calculateResults(testInvestmentWith);
        setResultsWithMultiplier(resultsWith);
        
        // Calcular resultados SIN Multiplicador™ (sin reinversión)
        const testInvestmentWithout = {
          ...investment,
          initialCertificates: required,
          years: timeframe,
          reinvestProfits: false
        };
        const resultsWithout = calculateResults(testInvestmentWithout);
        setResultsWithoutMultiplier(resultsWithout);
        
        // Actualizar el investment context
        updateInvestment({ 
          initialCertificates: required,
          years: timeframe,
          investorMonthlyGoal: monthlyGoal,
          investorTimeframe: timeframe
        });
        
        setIsCalculating(false);
      } catch (error) {
        console.error('Error in calculation:', error);
        setIsCalculating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    monthlyGoal, 
    timeframe, 
    currentReinvestmentSetting, // Agregar dependencia del estado de reinversión
    investment.averageProductionPerHectare, 
    investment.averageSalePricePerKg, 
    investment.increaseLemonPrice, 
    investment.lemonPriceIncrease, 
    investment.appreciationRate, 
    investment.certificateBasePrice
  ]);

  // Calcular métricas de inversión
  const totalInvestment = calculatedCertificates * investment.certificateBasePrice;
  const downPayment = totalInvestment * 0.3;
  const monthlyPayment = (totalInvestment - downPayment) / 48;

  // Handlers para incremento/decremento
  const handleIncrementGoal = () => {
    setMonthlyGoal(prev => Math.min(500000, prev + 10000));
  };

  const handleDecrementGoal = () => {
    setMonthlyGoal(prev => Math.max(10000, prev - 10000));
  };

  const handleIncrementTimeframe = () => {
    setTimeframe(prev => Math.min(30, prev + 1));
  };

  const handleDecrementTimeframe = () => {
    setTimeframe(prev => Math.max(5, prev - 1));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Target className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Meta del Inversionista</h2>
              <p className="text-green-100">Define tu objetivo de ingreso pasivo y descubre el poder del Multiplicador™</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Multiplicador™ Explanation */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">{compoundMultiplierContent.title}</h3>
                <p className="text-green-700">{compoundMultiplierContent.tagline}</p>
              </div>
            </div>
            
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-green-200">
              <p className="text-sm text-green-700 mb-3">
                <strong>¿Qué hace diferente al Multiplicador™?</strong> Mientras las inversiones tradicionales 
                solo multiplican dinero, el Multiplicador™ multiplica activos reales productivos.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="font-medium text-gray-700 mb-1">Interés Compuesto Tradicional:</div>
                  <div className="text-gray-600">$100K → $200K → $400K (solo números)</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="font-medium text-green-700 mb-1">Interés Compuesto Multiplicador™:</div>
                  <div className="text-green-600">1 certificado → 2 → 4 → 8 (activos reales)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Meta Mensual */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">¿Cuánto Ingreso Pasivo Deseas?</h3>
            </div>
            
            {/* Presets de Meta Mensual */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {monthlyGoalPresets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setMonthlyGoal(preset.value)}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    monthlyGoal === preset.value
                      ? 'border-green-500 bg-green-50 text-green-700 shadow-md transform scale-105'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-800 mb-1">
                      {formatCurrency(preset.value, investment.currencyFormat)}
                    </div>
                    <div className="text-sm font-medium text-gray-700">{preset.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{preset.description}</div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Control Manual de Meta */}
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementGoal}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={monthlyGoal <= 10000}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="10000"
                  max="500000"
                  step="5000"
                  value={monthlyGoal}
                  onChange={(e) => setMonthlyGoal(Math.max(10000, Math.min(500000, parseInt(e.target.value) || 50000)))}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-32 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  title="Meta de ingreso pasivo mensual que deseas alcanzar. El Multiplicador™ calculará automáticamente los certificados necesarios"
                />
                <div className="text-sm text-green-600">
                  {investment.currencyFormat} mensuales
                </div>
              </div>
              <button 
                onClick={handleIncrementGoal}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={monthlyGoal >= 500000}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="10000"
                max="500000"
                step="5000"
                value={monthlyGoal}
                onChange={(e) => setMonthlyGoal(parseInt(e.target.value))}
                className="w-full h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-lg appearance-none cursor-pointer"
                title="Desliza para ajustar tu meta de ingreso mensual"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatCurrency(10000, investment.currencyFormat)}</span>
                <span>{formatCurrency(500000, investment.currencyFormat)}</span>
              </div>
            </div>
          </div>

          {/* Plazo de Inversión */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">¿En Cuánto Tiempo Quieres Lograrlo?</h3>
            </div>
            
            {/* Presets de Plazo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {timeframePresets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setTimeframe(preset.value)}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    timeframe === preset.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md transform scale-105'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-800 mb-1">{preset.value}</div>
                    <div className="text-sm font-medium text-gray-700">{preset.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{preset.description}</div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Control Manual de Plazo */}
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementTimeframe}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={timeframe <= 5}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="5"
                  max="30"
                  value={timeframe}
                  onChange={(e) => setTimeframe(Math.max(5, Math.min(30, parseInt(e.target.value) || 15)))}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  title="Plazo en años para alcanzar tu meta. El Multiplicador™ optimiza automáticamente el número de certificados necesarios"
                />
                <div className="text-sm text-blue-600">años</div>
              </div>
              <button 
                onClick={handleIncrementTimeframe}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={timeframe >= 30}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="5"
                max="30"
                step="1"
                value={timeframe}
                onChange={(e) => setTimeframe(parseInt(e.target.value))}
                className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                title="Desliza para ajustar el plazo de inversión"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>5 años</span>
                <span>30 años</span>
              </div>
            </div>
          </div>

          {/* Presets de Producción Rápidos */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Presets de Producción Rápidos</h3>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => updateInvestment({ 
                  averageProductionPerHectare: 25000,
                  averageSalePricePerKg: 30
                })}
                className={`py-6 px-6 rounded-xl text-lg font-medium transition-all duration-200 ${
                  investment.averageProductionPerHectare === 25000 && investment.averageSalePricePerKg === 30
                    ? 'bg-gradient-to-br from-gray-400 to-gray-500 text-white shadow-md transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Escenario Conservador: 25,000 kg/ha × $30/kg = Producción estable con precios moderados"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">Conservador</div>
                  <div className="text-sm opacity-80">25,000 kg/ha × $30/kg</div>
                  <div className="text-xs mt-2 opacity-70">
                    {((25000 * 30 * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}% utilidad
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => updateInvestment({ 
                  averageProductionPerHectare: 30000,
                  averageSalePricePerKg: 35
                })}
                className={`py-6 px-6 rounded-xl text-lg font-medium transition-all duration-200 ${
                  investment.averageProductionPerHectare === 30000 && investment.averageSalePricePerKg === 35
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Escenario Moderado: 30,000 kg/ha × $35/kg = Crecimiento balanceado y sostenible"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">Moderado</div>
                  <div className="text-sm opacity-80">30,000 kg/ha × $35/kg</div>
                  <div className="text-xs mt-2 opacity-70">
                    {((30000 * 35 * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}% utilidad
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => updateInvestment({ 
                  averageProductionPerHectare: 35000,
                  averageSalePricePerKg: 38
                })}
                className={`py-6 px-6 rounded-xl text-lg font-medium transition-all duration-200 ${
                  investment.averageProductionPerHectare === 35000 && investment.averageSalePricePerKg === 38
                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Escenario Optimista: 35,000 kg/ha × $38/kg = Alto rendimiento con precios premium"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">Optimista</div>
                  <div className="text-sm opacity-80">35,000 kg/ha × $38/kg</div>
                  <div className="text-xs mt-2 opacity-70">
                    {((35000 * 38 * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}% utilidad
                  </div>
                </div>
              </button>
            </div>
            
            <div className="mt-4 p-4 bg-green-50 rounded-xl">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-1">Configuración Actual Seleccionada</div>
                <div className="text-lg font-bold text-green-700">
                  {investment.averageProductionPerHectare.toLocaleString()} kg/ha × ${investment.averageSalePricePerKg}/kg
                </div>
                <div className="text-sm text-green-600 mt-1">
                  Utilidad anual: {((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Toggle de Reinversión - Multiplicador™ */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Interés Compuesto Multiplicador™</h3>
                  <p className="text-sm text-green-600">Activa/desactiva para ver la diferencia</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${currentReinvestmentSetting ? 'text-green-700' : 'text-gray-500'}`}>
                  {currentReinvestmentSetting ? 'ACTIVADO' : 'DESACTIVADO'}
                </span>
                <button
                  onClick={() => {
                    const newSetting = !currentReinvestmentSetting;
                    updateInvestment({ reinvestProfits: newSetting });
                  }}
                  className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 ${
                    currentReinvestmentSetting ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                      currentReinvestmentSetting ? 'translate-x-9' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {/* Explicación del Multiplicador™ con números reales */}
            <div className="space-y-6">
              {currentReinvestmentSetting ? (
                <div>
                  <p className="text-sm text-green-700 mb-3">
                    <strong>✅ Multiplicador™ ACTIVADO:</strong> Las utilidades se reinvierten automáticamente 
                    para adquirir más certificados. Tus activos se multiplican exponencialmente.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="font-medium text-green-700 mb-1">Con Multiplicador™:</div>
                      <div className="text-green-600">
                        {calculatedCertificates} certificados → {resultsWithMultiplier?.certificatesSummary?.totalCertificates || calculatedCertificates} certificados
                      </div>
                      <div className="text-green-600">
                        (+{(resultsWithMultiplier?.certificatesSummary?.fromReinvestment || 0)} certificados GRATIS)
                      </div>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="font-medium text-green-700 mb-1">Ingreso Final:</div>
                      <div className="text-green-600">
                        {resultsWithMultiplier ? formatCurrency(resultsWithMultiplier.finalMonthlyIncome, investment.currencyFormat) : 'Calculando...'}
                      </div>
                      <div className="text-green-600 text-xs">
                        {resultsWithMultiplier ? `${((resultsWithMultiplier.finalMonthlyIncome / monthlyGoal) * 100).toFixed(1)}% de tu meta` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>❌ Multiplicador™ DESACTIVADO:</strong> Las utilidades se reciben como ingreso mensual 
                    sin reinvertir. Solo tienes los certificados que compraste inicialmente.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="font-medium text-gray-700 mb-1">Sin Multiplicador™:</div>
                      <div className="text-gray-600">
                        {calculatedCertificates} certificados → {calculatedCertificates} certificados
                      </div>
                      <div className="text-gray-600">
                        (Los mismos que compraste)
                      </div>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg">
                      <div className="font-medium text-red-700 mb-1">Ingreso Final:</div>
                      <div className="text-red-600">
                        {resultsWithoutMultiplier ? formatCurrency(resultsWithoutMultiplier.finalMonthlyIncome, investment.currencyFormat) : 'Calculando...'}
                      </div>
                      <div className="text-red-600 text-xs">
                        {resultsWithoutMultiplier ? `${((resultsWithoutMultiplier.finalMonthlyIncome / monthlyGoal) * 100).toFixed(1)}% de tu meta` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resultados del Cálculo */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-200 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-800">Cálculo Automático con Multiplicador™</h3>
                <p className="text-sm text-blue-600">
                  {isCalculating ? 'Calculando certificados necesarios...' : 'Certificados calculados automáticamente'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Meta Mensual */}
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Target className="w-4 h-4 text-green-600" />
                  </div>
                  <h4 className="font-medium text-gray-800">Meta Mensual</h4>
                </div>
                <div className="text-2xl font-bold text-green-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={monthlyGoal}
                    currencyFormat={investment.currencyFormat}
                    duration={1.2}
                    staggerDelay={0.08}
                  />
                </div>
                <div className="text-sm text-green-600 mt-1">ingreso pasivo deseado</div>
              </div>

              {/* Plazo */}
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <h4 className="font-medium text-gray-800">Plazo</h4>
                </div>
                <div className="text-2xl font-bold text-blue-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={timeframe}
                    currencyFormat="MXN"
                    duration={1.0}
                    staggerDelay={0.06}
                    isCurrency={false}
                  /> años
                </div>
                <div className="text-sm text-blue-600 mt-1">horizonte de inversión</div>
              </div>

              {/* Certificados Necesarios */}
              <div className="bg-white p-6 rounded-xl shadow-sm border-2 border-green-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Award className="w-4 h-4 text-purple-600" />
                  </div>
                  <h4 className="font-medium text-gray-800">Certificados Necesarios</h4>
                </div>
                <div className="text-3xl font-bold text-purple-700 relative overflow-hidden">
                  {isCalculating ? (
                    <div className="flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <AnimatedNumberDisplay
                      value={calculatedCertificates}
                      currencyFormat="MXN"
                      duration={1.4}
                      staggerDelay={0.1}
                      isCurrency={false}
                    />
                  )}
                </div>
                <div className="text-sm text-purple-600 mt-1">con Multiplicador™</div>
              </div>
            </div>
          </div>

          {/* Resumen de Inversión */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">Resumen de Inversión Calculada</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                <div className="text-sm text-gray-600 mb-1">Inversión Total</div>
                <div className="text-xl font-bold text-green-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={convertFromMXN(totalInvestment, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                    currencyFormat={investment.currencyFormat}
                    duration={1.2}
                    staggerDelay={0.08}
                  />
                </div>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                <div className="text-sm text-gray-600 mb-1">Enganche (30%)</div>
                <div className="text-xl font-bold text-blue-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={convertFromMXN(downPayment, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                    currencyFormat={investment.currencyFormat}
                    duration={1.2}
                    staggerDelay={0.08}
                  />
                </div>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                <div className="text-sm text-gray-600 mb-1">Mensualidad</div>
                <div className="text-xl font-bold text-purple-700 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={convertFromMXN(monthlyPayment, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                    currencyFormat={investment.currencyFormat}
                    duration={1.2}
                    staggerDelay={0.08}
                  />
                </div>
                <div className="text-xs text-purple-600 mt-1">48 meses</div>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                <div className="text-sm text-gray-600 mb-1">Multiplicador</div>
                <div className="text-xl font-bold text-orange-700">
                  {resultsWithMultiplier ? 
                    `${(resultsWithMultiplier.certificatesSummary.totalCertificates / calculatedCertificates).toFixed(1)}x` : 
                    'Calculando...'
                  }
                </div>
                <div className="text-xs text-orange-600 mt-1">activos reales</div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Anterior</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReinvestmentInvestorGoals;